export function isTestSession(session?: { is_test_session?: boolean; matter_num?: string | null } | null): boolean {
  return !!(session?.is_test_session || /\btest\b/i.test(session?.matter_num ?? ''));
}

/**
 * Whether a path belongs to one of the session flows — the live call, the
 * waiting room, identity verification, KBA, the signing preview.
 *
 * These are the screens where reloading the page costs the user something they
 * cannot get back: a joined video call the other participants are watching,
 * answers half-typed into a KBA quiz, a signature in progress. Anything that
 * would reload on its own has to ask first here.
 *
 * @see docs/stale-build-recovery.md
 */
export function isSessionPath(path?: string | null): boolean {
  return /^\/sessions(\/|$)/.test(path ?? '');
}

/**
 * When Daily will remove the local participant from the call, or null when
 * nothing is going to remove them.
 *
 * The backend caps every meeting token with `eject_after_elapsed`, counted from
 * the moment the participant joins, so the deadline is only knowable once they
 * are in the call. Daily also reports its own `will_eject_at` on the
 * participant, which is preferred when it is real: it is the server's own
 * answer, and it survives a clock the browser adjusted mid-call. It is not
 * always there — daily-js types it as always present but never computes it, it
 * arrives from the call machine, and it reads as the epoch when no ejection is
 * scheduled — so a value that is not in the future is ignored rather than
 * trusted.
 */
export function resolveEjectAt({
  willEjectAt,
  joinedAt,
  maxSessionSeconds,
}: {
  willEjectAt?: Date | null;
  joinedAt?: Date | null;
  maxSessionSeconds?: number | null;
}): Date | null {
  if (willEjectAt instanceof Date && willEjectAt.getTime() > Date.now()) {
    return willEjectAt;
  }

  if (!joinedAt || !maxSessionSeconds) {
    return null;
  }

  return new Date(joinedAt.getTime() + maxSessionSeconds * 1000);
}
